// Global App Context Provider
import { createContext, useState, useCallback, useEffect } from 'react';
import { SCREENS, ANALYSIS_TYPES } from '../config/constants';
import { storage } from '../services/storage';
import sessionCache from '../services/sessionCache';

export const AppContext = createContext();

export function AppProvider({ children }) {
    // Navigation state
    const [currentScreen, setCurrentScreen] = useState(SCREENS.SPLASH);
    const [previousScreen, setPreviousScreen] = useState(null);

    // Analysis state (initialized from session cache if available)
    const [analysisType, setAnalysisType] = useState(() => sessionCache.getCachedAnalysisType() || ANALYSIS_TYPES.FULL);
    const [questionnaireAnswers, setQuestionnaireAnswers] = useState(() => sessionCache.getCachedAnswers() || {});
    const [capturedImages, setCapturedImages] = useState(() => sessionCache.getCachedImages() || {});
    const [analysisResults, setAnalysisResults] = useState(null);
    const [analysisData, setAnalysisData] = useState(null); // For verification flow
    const [rawAIResponse, setRawAIResponse] = useState(null); // Raw AI response for audit

    // Persistent state (loaded from storage)
    const [baselineData, setBaselineData] = useState(() => storage.getBaselineData());
    const [calibrationData, setCalibrationData] = useState(() => storage.getCalibrationData());
    const [history, setHistory] = useState(() => storage.getHistory());

    // UI state
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

    // Navigate to screen
    const navigate = useCallback((screen) => {
        setPreviousScreen(currentScreen);
        setCurrentScreen(screen);
    }, [currentScreen]);

    // Go back
    const goBack = useCallback(() => {
        if (previousScreen) {
            setCurrentScreen(previousScreen);
            setPreviousScreen(null);
        } else {
            setCurrentScreen(SCREENS.HOME);
        }
    }, [previousScreen]);

    // Show toast notification
    const showToast = useCallback((message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    }, []);

    // Save to history
    const saveToHistory = useCallback((result) => {
        const newHistory = [{
            id: Date.now(),
            date: new Date().toISOString(),
            type: analysisType,
            score: result.overallScore,
            ...result,
        }, ...history].slice(0, 50);
        setHistory(newHistory);
        storage.setHistory(newHistory);
    }, [history, analysisType]);

    // Update baseline
    const updateBaseline = useCallback((data) => {
        const newBaseline = { ...baselineData, ...data };
        setBaselineData(newBaseline);
        storage.setBaselineData(newBaseline);
    }, [baselineData]);

    // Reset analysis state (for new analysis)
    const resetAnalysis = useCallback(() => {
        setQuestionnaireAnswers({});
        setCapturedImages({});
        setAnalysisResults(null);
        setAnalysisData(null);
        setRawAIResponse(null);
        sessionCache.clearSession();
    }, []);

    // Auto-save to session cache when state changes
    useEffect(() => {
        if (Object.keys(capturedImages).length > 0) {
            sessionCache.saveCapturedImages(capturedImages);
        }
    }, [capturedImages]);

    useEffect(() => {
        if (Object.keys(questionnaireAnswers).length > 0) {
            sessionCache.saveAnswers(questionnaireAnswers);
        }
    }, [questionnaireAnswers]);

    useEffect(() => {
        sessionCache.saveAnalysisType(analysisType);
    }, [analysisType]);

    const value = {
        // Navigation
        currentScreen,
        setCurrentScreen: navigate,
        goBack,

        // Analysis
        analysisType,
        setAnalysisType,
        questionnaireAnswers,
        setQuestionnaireAnswers,
        capturedImages,
        setCapturedImages,
        analysisResults,
        setAnalysisResults,
        analysisData,
        setAnalysisData,
        rawAIResponse,
        setRawAIResponse,
        resetAnalysis,

        // Persistent data
        baselineData,
        updateBaseline,
        calibrationData,
        setCalibrationData,
        history,
        saveToHistory,

        // UI
        toast,
        showToast,
    };

    return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}
